import { useEffect, useState } from "react";
import { ArrowRight, LockKeyhole, NotebookPen, Sparkles, Video } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { api, getStoredToken, setAuthSession } from "@/lib/portalApi";

const platformPoints = [
  "Access weekly homework, practice SACs, practice exams, and worked solutions.",
  "Watch official exam walkthroughs, practice SAC walkthroughs, CAS skills videos, and performance guides.",
  "Upload homework as images, PDFs, or Word documents before the due date.",
  "Use the Ascend Academy AI for immediate explanations when you are stuck.",
];

const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(" | ");
  }
  if (detail && typeof detail === "object") {
    return detail.message || JSON.stringify(detail);
  }
  return fallback;
};

export const PortalLoginPage = () => {
  const navigate = useNavigate();
  const search = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const mode = search.get("mode");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const boot = async () => {
      const token = getStoredToken();
      if (!token) return;

      try {
        const response = await api.get("/auth/me");
        navigate(response.data.role === "admin" ? "/portal/admin" : "/portal/student");
      } catch {
        localStorage.removeItem("ascend-platform-token");
      }
    };

    boot();
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await api.post("/auth/login", { identifier, password });
      setAuthSession(response.data);
      toast.success("Login successful");
      navigate(response.data.redirect_path);
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to log in"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f7f2]" data-testid="platform-login-page">
      <div className="ascend-container py-10">
        <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
          <Link to="/" className="font-display text-3xl text-[#002147]" data-testid="platform-login-home-link">
            Ascend Academy
          </Link>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" data-testid="platform-login-homepage-student-button">
              <Link to="/platform">Student Login</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" data-testid="platform-login-homepage-admin-button">
              <Link to="/platform?mode=admin">Admin Login</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" data-testid="platform-login-back-button">
              <Link to="/">Back to Main Website</Link>
            </Button>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
          <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="platform-login-overview-card">
            <CardContent className="space-y-8 p-8 md:p-10">
              <div className="space-y-4">
                <p className="eyebrow text-[#f3d8a1]" data-testid="platform-login-overline">Ascend Academy Platform</p>
                <h1 className="font-display text-4xl tracking-tight text-white md:text-5xl" data-testid="platform-login-heading">
                  A private student hub for resources, submissions, walkthroughs, and AI support.
                </h1>
                <p className="text-sm leading-7 text-white/82 md:text-base" data-testid="platform-login-description">
                  Access is issued directly by Ascend Academy. Students cannot create their own accounts; login details are created for enrolled students only.
                </p>
              </div>

              <div className="grid gap-4" data-testid="platform-login-points">
                {platformPoints.map((point, index) => (
                  <div key={point} className="flex gap-3 border border-white/12 bg-white/5 p-4" data-testid={`platform-login-point-${index + 1}`}>
                    {index === 0 ? <NotebookPen className="mt-1 h-4 w-4 shrink-0 text-[#f3d8a1]" /> : null}
                    {index === 1 ? <Video className="mt-1 h-4 w-4 shrink-0 text-[#f3d8a1]" /> : null}
                    {index === 2 ? <LockKeyhole className="mt-1 h-4 w-4 shrink-0 text-[#f3d8a1]" /> : null}
                    {index === 3 ? <Sparkles className="mt-1 h-4 w-4 shrink-0 text-[#f3d8a1]" /> : null}
                    <p className="text-sm leading-7 text-white/84">{point}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="platform-login-form-card">
            <CardContent className="p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="platform-login-form">
                <div className="space-y-3">
                  <p className="eyebrow text-[#002147]/60" data-testid="platform-login-form-overline">Secure access</p>
                  <h2 className="font-display text-4xl text-[#002147]" data-testid="platform-login-form-heading">
                    {mode === "admin" ? "Admin login for Ascend Academy." : "Sign in with your Ascend login details."}
                  </h2>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-[#10233d]" data-testid="platform-login-identifier-label">
                      Username or email
                    </label>
                    <Input
                      value={identifier}
                      onChange={(event) => setIdentifier(event.target.value)}
                      placeholder="Enter your issued login"
                      className="rounded-sm border-[#002147]/15 bg-[#fcfbf8]"
                      data-testid="platform-login-identifier-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-[#10233d]" data-testid="platform-login-password-label">
                      Password
                    </label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Enter your password"
                      className="rounded-sm border-[#002147]/15 bg-[#fcfbf8]"
                      data-testid="platform-login-password-input"
                    />
                  </div>
                </div>

                <div className="border border-[#002147]/10 bg-[#f8fafc] p-4 text-sm leading-7 text-slate-700" data-testid="platform-login-note">
                  If you are an Ascend Academy student and do not yet have login details, please contact the academy directly. There is no public sign-up on this platform.
                </div>

                <Button type="submit" disabled={loading} className="w-full rounded-sm bg-[#002147] text-white hover:bg-[#001530]" data-testid="platform-login-submit-button">
                  {loading ? "Signing in..." : "Enter Platform"}
                  {!loading ? <ArrowRight className="ml-1 h-4 w-4" /> : null}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};